// Shared types: the contract between server/ and app/. Change only with the lead.

export type CharId = 'sunny' | 'margo' | 'arjun' | 'tavo';
export const CHAR_IDS: CharId[] = ['sunny', 'margo', 'arjun', 'tavo'];

export type ClientKind = 'web' | 'ios' | 'android' | 'mac' | 'windows' | 'desktop-web';
export type CheckIn = 'light' | 'steady' | 'close';
export type StuckReason = 'big' | 'start' | 'energy' | 'distract' | 'worry' | 'time';
export type LaterWhen = 'In 1 hour' | 'After lunch' | 'Tonight' | 'Tomorrow';

/** Onboarding answers (O2-O5, O10). Labels are the exact option labels from SPEC §7 Group A. */
export interface OnboardingAnswers {
  change: string;          // O2 option label, e.g. 'Get work done on time', or the user's own words when no option matched
  changeSaid?: string;     // optional transcript of "say it in your own words"
  stuck: string;           // O3, 'Starting' | 'Keeping going' | 'Finishing' | 'Remembering'
  tone: string[];          // O4, up to two of 'Warm and kind' | 'Straight to the point' | 'Make me laugh' | 'Push me a little'
  checkin: CheckIn;        // O5, default 'steady'
  planTime: string;        // O10, '9:00 AM'
}

export type CardState = 'todo' | 'done' | 'moved' | 'removed';
export type CardSource = 'plan' | 'chat' | 'stuck' | 'nudge' | 'onboarding' | 'carry';

export interface Card {
  id: string;
  day: string;              // day key (YYYY-MM-DD) the card belongs to
  at: number;               // minutes after local midnight, e.g. 570 = 9:30 AM
  time: string;             // display, '9:30 AM'
  title: string;
  min: number;              // planned minutes
  st: CardState;
  source: CardSource;
  order: number;
  from?: string | null;     // parent card id when it came from a split
  startedAt?: string | null; // ISO
  doneAt?: string | null;   // ISO
  stuck?: boolean;          // Stuck was used on this card (for "Got past stuck")
  alt?: string | null;      // smaller version chosen in Stuck; cowork shows it instead of the title
  movedTo?: string | null;  // day key, when st = 'moved'
  note?: string | null;     // small note shown on the done card (e.g. 'THE Q3 REPORT HAS A FIRST SENTENCE.')
}

/** A card the buddy proposes before it becomes real (plan message, nudge offer, split preview). */
export interface ProposedCard { time: string; at: number; title: string; min: number }

export type MsgKind =
  | 'text'       // plain bubble
  | 'plan'       // plan message card (data: PlanData)
  | 'planned'    // confirmation line after "Looks good"
  | 'change'     // talk changed cards (data: ChangeData)
  | 'summary'    // evening wrap (data: SummaryData)
  | 'offer'      // n3 "two minutes while we talk?" (data: { status })
  | 'nudge'      // morning follow-up with one small card (data: { card: ProposedCard, status })
  | 'tomorrow'   // "FIRST ON TOMORROW'S LIST" card (data: { card: ProposedCard })
  | 'crisis'     // crisis card (M6) was shown
  | 'limit'      // free message limit reached (M4)
  | 'week'       // weekly recap ready
  | 'unlock'     // a route stop opened (data: RouteStop)
  | 'stuck'      // Stuck talked through in chat, role 'buddy' (data: StuckChatData)
  | 'done'       // a card finished from chat, role 'system' (data: DoneNoteData)
  | 'team';      // a small note from the Xiaoman team (admin action), role 'system' (data: TeamNoteData). Never in
                 // the buddy's voice: render it as a system card, not as her bubble.

export interface Message {
  id: string;
  ts: string;               // ISO
  role: 'buddy' | 'user' | 'system';
  ch: CharId;
  kind: MsgKind;
  text: string;
  voice?: boolean;          // user message came from hold-to-talk
  image?: string | null;    // user photo (data URL thumbnail or asset URL)
  data?: any;
}

export interface PlanData { cards: ProposedCard[]; status: 'pending' | 'accepted' | 'replaced' }
export interface ChangeItem { type: 'moved' | 'split' | 'removed' | 'added' | 'retimed'; title: string; detail: string; cardIds: string[] }
export interface ChangeData { changeId: string; items: ChangeItem[]; undone?: boolean }
export interface SummaryData {
  done: number; moved: number; total: number; perfect: boolean; cards: { title: string; st: CardState }[];
  mark?: DayMark;   // that day's streak mark: 'today' (evening, not final yet), then lit / rest / freeze / missed once the day is over
  streak?: number;  // streak count through that day
}

export interface MemoryItem { id: string; text: string; ts: string }

export type StopType = 'keep' | 'chap' | 'place' | 'sound' | 'season';
export interface RouteStop {
  idx: number;              // 0-based position on the route
  type: StopType;
  label: string;            // 'Chapter 3: Pick a sound' | "A new look for Margo's theater" | keepsake name | sound name
  level: number;            // absolute level at which this stop opens for this user and buddy
  reached: boolean;
  ready: boolean;           // content exists in this build (month-one scope)
  keep?: number;            // keepsake sheet index (0-7) for type 'keep'
  chap?: number;            // chapter number (2-7) for type 'chap'
  place?: number;           // stage number (2-7) for type 'place'
  sound?: string;           // sound id for type 'sound'
}

export interface BuddyState {
  ch: CharId;
  met: boolean;
  daysTogether: number;
  closeness: 0 | 1 | 2 | 3 | 4; // Just met .. Close
  stops: number;            // route stops reached with this buddy
  stage: number;            // place stage 1..7
  chaptersOpen: number;     // 1..7
  keepsakes: number[];      // sheet indices earned, in route order
  sounds: string[];         // sound ids earned
  route: RouteStop[];       // whole season-1 route with status
  chapterChoices: Record<string, string>; // chapter number -> choice text
  newChapter: number | null; // newest unread chapter, for the NEW tag
  memory: MemoryItem[];
  relationship: 'buddy' | 'romance';
}

export interface LevelInfo { level: number; into: number; need: number }

export type DayMark = 'lit' | 'rest' | 'freeze' | 'missed' | 'today' | 'future';
export interface StreakState {
  count: number;
  litToday: boolean;
  best: number;
  freezeLeft: number;       // this ISO week
  week: { day: string; label: string; mark: DayMark }[]; // Mon..Sun of the current week
  safeNote?: string | null; // e.g. "Tuesday was a rest day, and Wednesday used your weekly freeze. Nothing to make up."
}

export interface PlanState {
  day: string;
  status: 'none' | 'asked' | 'proposed' | 'planned';
  cards: Card[];            // today's cards (all states), ordered by `at` then `order`
  perfectGiven: boolean;
}

export interface WeekState {
  start: string; end: string; label: string;   // 'Nov 2 – Nov 8'
  ready: boolean;           // Sunday recap available
  lit: number; cards: number; onTime: number; onTimePrev: number;
  highlight: string | null; // e.g. 'Thursday you started before Margo even asked.'
  answer: string | null; reply: string | null;
  chest: 'locked' | 'ready' | 'opened';
}

export interface Friend { id: string; name: string; initial: string; color: string; together: number; status: 'done' | 'not-yet' | 'new'; working?: string | null }
export interface FriendsState { friends: Friend[]; club: { name: string; done: number; goal: number }; sent: Record<string, string> }

export interface UserInfo {
  id: string; email: string; createdAt: string; tz: string; invite: string;
  /** true (default): `tz` follows the device that last opened the app (X-TZ). false: the user picked `tz` in Me and
   *  no device changes it, so phone and computer agree on "today". */
  tzAuto: boolean;
  vip: boolean; planTime: string; checkin: CheckIn; reminders: boolean;
  ob: OnboardingAnswers | null;
  debugDays: number;
}

/** PATCH /api/cards/:id: change a card that is still to do (a misheard title, the wrong time). Omitted = unchanged. */
export interface CardEdit { title?: string; time?: string; min?: number }

export interface Bonus { label: 'Lit today' | 'On time' | 'Got past stuck' | 'Back to back'; pts: number }

/** Built by the server when a card is done; drives R (QR1_Reward). */
export interface Reward {
  id: string; title: string; time: string; doneAt: string;
  base: number; bonus: Bonus[]; gained: number;
  minutes: number;                         // minutes worked (start -> done, at least 1)
  direct?: boolean;                        // done without Start (ticked off): no "minutes worked" to show
  dayDone: number; dayTotal: number;
  streakFrom: number; streakTo: number | null; // null if today was already lit
  flameGrew: boolean; nextFlameIn: number | null;
  perfect: boolean;                        // +30 and the gift beat
  levelFrom: number; levelTo: number;
  unlock: RouteStop | null;                // stop opened by this level-up (null if none or not ready)
  pointsBefore: number; pointsAfter: number;
  line: string;                            // the buddy's done line (signature humor)
}

export type StuckHelp =
  | { reason: 'big'; line: string; split: ProposedCard[] | null; firstStep: string | null }
  | { reason: 'start'; line: string; firstStep: string }
  | { reason: 'energy'; line: string; alt: string }
  | { reason: 'distract'; line: string }
  | { reason: 'worry'; line: string; alt: string }
  | { reason: 'time'; line: string; chips: LaterWhen[] };

/** O8: where the user is with their thing. `where` in a request is their own answer (new / middle / almost);
 *  `stage` in a reply is what the buddy went on ('none' = a one-off with no middle, like a call; 'unknown' = can't
 *  tell, so the card fits any stage). */
export type FirstWhere = 'new' | 'middle' | 'almost';
export type FirstStage = FirstWhere | 'none' | 'unknown';

/** Onboarding O8 first help (and its branches). */
export interface FirstHelp {
  reply: string;            // buddy reply after the user said their thing
  card: string;             // first card title (2 min)
  smaller: string;          // smaller version
  thing: string;            // short name of the real task, e.g. 'The Q3 report'
  stage?: FirstStage | null; // where the buddy thinks they are with it (the card never restarts work under way)
  prep?: { line: string; card: string; label: string } | null;    // 'can't now' prep step
  tomorrow?: ProposedCard | null;                                  // first card of tomorrow's plan
  pickTime?: { line: string; card: string; options: { label: string; at: number; day: 'today' | 'tomorrow' }[] } | null;
}

export interface RegisterRequest {
  invite: string; email: string; password: string; tz: string;
  ch: CharId; ob: OnboardingAnswers;
  first: {
    thing: string;            // what they said (short)
    said: string;             // full transcript
    card: string;             // card title done in O9 (or the 'pick a time' card)
    done: boolean;            // first card done (or time picked) -> lights day 0
    doneAt?: string | null;
    minutes?: number | null;
    note?: string | null;     // e.g. 'TIME PICKED'
    tomorrow?: ProposedCard | null;
    tomorrowDay?: 'today' | 'tomorrow' | null; // 'today' = O8 "Tonight 8:00 PM" pick: the card goes on day 0 (default 'tomorrow')
    reply?: string | null;    // FirstHelp.reply shown in O8, kept in the chat history (optional)
  };
  reminders?: boolean;        // O11 "Let {n} send reminders" (default true)
}

/** `id` is the note id (job key such as `morning:2026-11-02`, `card:<cardId>:0`); every push payload carries it as
 *  `noteId`, so the app can report `push_open`. 'alert' = an admin alert ("小满后台") to the uids in ALERT_UIDS. */
export interface Notify { id: string; kind: 'morning' | 'nudge' | 'card' | 'checkin' | 'evening' | 'week' | 'recall' | 'test' | 'alert';
  title: string; body: string; ch: CharId; at: string; data?: any; actions?: { id: string; label: string }[] }
export interface LocalNote { id: string; at: string; title: string; body: string; data?: any }

export interface ChatHints {
  back: boolean;            // away 2+ days: show the welcome-back line and the "streak is safe" note
  offerToday: boolean;      // n3 offer already made today (never twice)
  evening: boolean;         // evening wrap available
  firstNight: boolean;      // day 0 after onboarding
}

export interface AppState {
  v: number;                // monotonic version
  now: string;              // ISO, debug offset applied
  today: string;            // day key
  dateLabel: string;        // 'Tuesday, Nov 10'
  dayIndex: number;         // days since signup (day 0 = signup day)
  user: UserInfo;
  ch: CharId;
  buddy: BuddyState;        // current buddy
  buddies: Record<CharId, { met: boolean; daysTogether: number; stops: number; stage: number }>;
  streak: StreakState;
  points: number;
  level: LevelInfo;
  plan: PlanState;
  tomorrow: Card[];
  messages: Message[];      // recent chat (oldest first)
  hints: ChatHints;
  usage: { messagesToday: number; freeLimit: number; costTotalUsd: number; costTodayUsd: number };
  week: WeekState | null;
  collection: { cardsDone: number; recent: { id: string; title: string; day: string; doneAt: string }[] };
  friends: FriendsState;
  reward: Reward | null;    // last reward not yet seen
  tomorrowPlanTime: string; // '9:00 AM'
}

export type ChatEvent =
  | { t: 'user'; message: Message }            // echo, with transcript when audio was sent
  | { t: 'delta'; text: string }               // streaming text of the buddy reply
  | { t: 'message'; message: Message }         // a finished buddy/system message (may be a plan/change card)
  | { t: 'limit' }                             // free limit reached; show M4
  | { t: 'crisis' }                            // show M6 now
  | { t: 'error'; message: string; code?: ChatErrorCode }
  | { t: 'end'; state: AppState };

/** Why a turn stopped before the user's message was saved (on `error` events; absent = anything else).
 *  'stt': the voice message could not be turned into text: the app keeps the clip and offers to send it again.
 *  'no_words': it was turned into text but held no words (too short, too noisy).
 *  'region': the AI is not available where the user is. */
export type ChatErrorCode = 'stt' | 'no_words' | 'region';

// ------------------------------------------------------------------ feedback (internal beta: the β button)
// POST /api/feedback works with or without an account (onboarding comes first). The inbox lives in the Directory DO;
// /admin and the admin API read it. Statuses are set during triage (see AGENTS.md "Feedback").
export type FeedbackStatus = 'new' | 'doing' | 'decide' | 'fixed' | 'idea' | 'needinfo' | 'closed';
export const FEEDBACK_STATUSES: FeedbackStatus[] = ['new', 'doing', 'decide', 'fixed', 'idea', 'needinfo', 'closed'];
/** Set during triage: something broken, something that feels off, a new idea. */
export type FeedbackKind = 'bug' | 'ux' | 'idea' | 'other';
export const FEEDBACK_KINDS: FeedbackKind[] = ['bug', 'ux', 'idea', 'other'];

export interface FeedbackImage { b64: string; mime: string }

export interface FeedbackRequest {
  text: string;                  // what the tester said (typed, or the transcript of hold-to-talk)
  voice?: boolean;               // text came from hold-to-talk
  shot?: FeedbackImage | null;   // screenshot of the screen the β was tapped on (JPEG)
  photos?: FeedbackImage[];      // pictures the tester added (max 2), e.g. a notification
  context: Record<string, unknown>; // what the app knew: client, build, screen, trail, errors, cards, recent chat...
  invite?: string;               // before an account exists
}

export interface FeedbackFile { n: number; role: 'shot' | 'photo'; mime: string; bytes: number }

export interface FeedbackItem {
  id: string; createdAt: string; updatedAt: string;
  uid: string | null; email: string | null; invite: string | null;
  client: string | null; build: string | null; screen: string | null;
  text: string; voice: boolean;
  status: FeedbackStatus; kind: FeedbackKind | null; note: string | null;
  files: FeedbackFile[];
  context?: Record<string, unknown>;
  /** Added by hand in the admin (POST /api/admin/feedback): where it came from. null = the app's β button. */
  source?: 'wechat' | 'dm' | 'in_person' | null;
  /** Added by hand: who said it (free text), when it is not tied to an account. */
  who?: string | null;
}

/** Uncaught page errors the app reports by itself (POST /api/client-errors, max 10 per call). */
export interface ClientErrorReport { message: string; stack?: string; screen?: string; at?: string }
export interface ClientErrorsRequest { client: string; build: string; errors: ClientErrorReport[] }
export interface ClientErrorGroup {
  sig: string; firstAt: string; lastAt: string; count: number;
  message: string; stack: string | null; client: string | null; build: string | null; screen: string | null;
  users: number;
}

// ------------------------------------------------------------------ 0.2.0: stuck and done in chat (phone v2, desktop v3)
// Server-written chat messages (no model call, not counted toward the free limit). Copy: shared/src/content/stuck.ts.

/** Stuck, talked through in chat. One message per step, role 'buddy', text = the buddy's line for that step. */
export interface StuckChatData {
  cardId: string;
  title: string;                 // card title when it started
  step: 'ask' | 'help';          // ask: the six reasons are offered; help: the help for one reason
  reason: StuckReason | null;    // ask: the reason picked on this card (null while waiting); help: the reason helped
  help?: StuckHelp | null;       // step 'help' only (existing StuckHelp)
  out?: string | null;           // step 'help': outcome once acted on, e.g. 'Moved “Call the dentist” to after lunch',
                                 // 'Working on it with Margo: Write the two lines you’ll say, then dial'
  status: 'open' | 'resolved' | 'closed';  // closed = Never mind / Something else / superseded / card no longer todo
  act?: 'start' | 'later' | 'split' | null; // step 'help': the action that resolved it
  said?: string | null;          // id of the user's line right before this message ("I'm stuck on …", "{reason}.",
                                 // "Something else."), so a client can hide the pair after Never mind
}

/** A card finished from chat (ticked there, or a Working together started from chat). role 'system'. */
export interface DoneNoteData {
  cardId: string; title: string; gained: number;
  how: 'tick' | 'work';          // 'You ticked off “…” · +15 points' | 'You finished “…” with Margo · +15 points' (= text)
  line: string;                  // the buddy's done line (Reward.line), shown as her bubble under the tick line
  rewardId: string;
  undone?: boolean;              // set by Undo: the app hides the note
}

/** POST /api/cards/:id/done { from }: where the card was finished, so the server adds the 'done' note to the chat. */
export type DoneFrom = 'chat' | 'work-chat';

/** POST /api/stuck/:messageId */
export type StuckAction =
  | { action: 'start' }                       // start the help's step (alt = help.alt / firstStep, or none)
  | { action: 'later'; when: LaterWhen }      // 'time' help chips
  | { action: 'split' }                       // 'big' help with split cards
  | { action: 'again' }                       // "Something else": close this help, new ask card for the same card
  | { action: 'close' };                      // "Never mind": close the ask card

/** kind 'team' (role 'system'): a note from the Xiaoman team after an admin action (restore a streak, add a freeze,
 *  add points). The text is the team's words in English; it is not the buddy talking. */
export interface TeamNoteData { from: 'team'; action: 'restore_streak' | 'add_freeze' | 'add_points'; points?: number; day?: string }

// ------------------------------------------------------------------ admin P0 (analytics events, onboarding steps, admin API)
// docs/ADMIN.md is the reference. Events land in the `Analytics` Durable Object; names are the contract.

/** Events the app reports with POST /api/events { events: AppEvent[] } (max 50 per call; unknown kinds are dropped). */
export type AppEventKind = 'app_open' | 'app_bg' | 'push_open' | 'cowork_end' | 'film_mode' | 'share_card';
export const APP_EVENT_KINDS: AppEventKind[] = ['app_open', 'app_bg', 'push_open', 'cowork_end', 'film_mode', 'share_card'];
/** `at` = client time (ms). data per kind: app_open { from: 'launch'|'resume'|'push', noteId? } · app_bg { secs } ·
 *  push_open { noteId, kind } · cowork_end { card, minutes, how: 'done'|'close'|'stuck' } · film_mode { on } ·
 *  share_card { variant: 'card'|'streak'|'week', hideTask, action: 'open'|'save'|'share' } */
export interface AppEvent { kind: AppEventKind | string; at?: number; data?: Record<string, unknown> }
export interface EventsRequest { events: AppEvent[] }

/** Where a card action came from (optional `from` in the body of start / later / remove / stuck / stuck-chat). */
export type ActionFrom = 'today' | 'chat' | 'cowork' | 'next' | 'push' | 'tray' | 'mini';

/** POST /api/onboarding/step { invite, device, step, data } (no account yet; `device` = a random id the app keeps). */
export type OnboardingStep =
  | 'o1_view' | 'o1_ok' | 'o2' | 'o3' | 'o4' | 'o5' | 'o6_ok' | 'o6_change' | 'o7' | 'o8_card' | 'o8_accept' | 'o8_smaller'
  | 'o8_notquite' | 'o8_cantnow' | 'o9_start' | 'o9_done' | 'o10_lit' | 'o10_time' | 'o11_saved';
export const ONBOARDING_STEPS: OnboardingStep[] = ['o1_view', 'o1_ok', 'o2', 'o3', 'o4', 'o5', 'o6_ok', 'o6_change', 'o7', 'o8_card', 'o8_accept',
  'o8_smaller', 'o8_notquite', 'o8_cantnow', 'o9_start', 'o9_done', 'o10_lit', 'o10_time', 'o11_saved'];
export interface OnboardingStepRequest { invite: string; device: string; step: OnboardingStep; data?: Record<string, unknown> }

/** Admin roles: owner = everything; team = read-only without chat text or crisis, may set group/test, send a test push
 *  and triage feedback; agent (Claude's key) = reads everything incl. chat text except crisis days, updates feedback,
 *  runs read-only queries. */
export type AdminRole = 'owner' | 'team' | 'agent';
export interface AdminMe { id: string; email: string | null; name: string; role: AdminRole }
export type UserGroup = 'team' | 'friend' | 'external';

export interface AdminUserFlag { kind: 'push_none' | 'old_shell' | 'away' | 'morning_paused' | 'ai_region' | 'flagged' | 'test' | 'debug'; label: string; level: 'info' | 'warn' | 'crit'; days?: number; n?: number; country?: string | null }
export interface AdminDevice { platform: string; shell: string | null; build: string | null; lastSeen: string | null }
export interface AdminUser {
  id: string; email: string; group: UserGroup | null; test: boolean; debug: boolean; buddy: CharId | null;
  devices: AdminDevice[]; lastSeen: string | null; streak: number | null; costToday: number; costTotal: number; flags: AdminUserFlag[];
  // kept from the old list (the earlier /admin page reads these)
  createdAt: string; invite: string; costUsd: number; ch: string | null; level: number | null; points: number | null; tempPassword: boolean;
}

export type TimelineKind = 'push' | 'open' | 'chat' | 'model' | 'card' | 'err' | 'feedback' | 'flag' | 'admin';
export interface TimelineEntry {
  ts: string;               // ISO, real time
  t: string;                // 'HH:MM' in the user's time
  kind: TimelineKind;
  title: string;            // Chinese (admin UI language); card titles and chat stay as written
  detail?: string | null;
  text?: string | null;     // chat text: owner and agent only, never on a crisis day
  masked?: boolean;         // a crisis day: the owner can open it (POST users/:id/crisis-open, audited)
  who?: 'user' | 'char' | 'sys';
  refs?: { feedbackId?: string; flagId?: number; msgId?: string; cardId?: string; noteId?: string };
}

export type FlagKind = 'long' | 'multi_q' | 'late_plan' | 'joke_at_user' | 'not_smaller' | 'wrong_fact' | 'romance_first' | 'complaint' | 'crisis';
export const FLAG_KINDS: FlagKind[] = ['long', 'multi_q', 'late_plan', 'joke_at_user', 'not_smaller', 'wrong_fact', 'romance_first', 'complaint', 'crisis'];
export type FlagStatus = 'open' | 'good' | 'bad';
export interface ReviewItem {
  id: number; kind: FlagKind; uid: string; email: string | null; buddy: CharId | null; ts: string; day: string;
  excerpt: string | null; status: FlagStatus; why: string | null;
  context: { who: 'user' | 'char' | 'sys'; text: string; flagged: boolean }[] | null;
  masked: boolean;
  reason?: string | null;      // the reviewer's words with good / bad
  feedbackId?: string | null;  // the feedback item made from it (POST review/:id/feedback)
}

export interface AdminAlert { id?: number; ts: string; level: 'info' | 'warn' | 'crit'; rule: string; text: string; link: string | null }
export interface DeployRec { ts: string; version: string; note: string | null }
export interface ExpiryItem { name: string; now: string; until: string; daysLeft: number | null; status: string }
export interface ManualFeedbackRequest { uid?: string | null; who?: string | null; source: 'wechat' | 'dm' | 'in_person'; words: string; images?: FeedbackImage[] }
